import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Col, Container, Badge } from 'react-bootstrap';
import { FaSave, FaTimes, FaCheck, FaArrowLeft, FaPrint } from 'react-icons/fa';

import api from '../../services/api';
import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';
import { useMedico } from '../../hooks/medico';
import {
  useReceita,
  IReceita,
  IMedicamento,
  ICreateReceitaDTO,
  ICreateMedicamentoDTO,
  IUpdateReceitaDTO,
} from '../../hooks/receita';
import Loading from '../../components/Loading';
import SelectAsync from '../../components/SelectAsync';
import Input from '../../components/Input';
import TextArea from '../../components/TextArea';
import MedicamentoLista from '../../components/Receita/MedicamentoLista';

import getValidationErrors from '../../utils/getValidationErrors';

import {
  Titulo,
  FormSection,
  FormRow,
  ActionsBar,
  StatusAlert,
} from './styles';
import SelectSync from '../../components/SelectSync';
import { formataCPF, formataTelefone } from '../../utils/funcoes';

const TIPO_USUARIO = {
  ADMINISTRADOR: 1,
  SUPERVISOR: 2,
  ATENDENTE_CAIXA: 3,
  ATENDENTE: 4,
  MEDICO: 6,
};

interface IFormReceita {
  pacienteID: string;
  medicoID: string;
  RecPacNome: string;
  RecPacTelefone: string;
  RecPacCPF: string;
  RecPacEndereco: string;
  RecUsuNome: string;
  RecUsuCRM: string;
  RecUsuEndereco: string;
  RecInformacao: string;
}

const ReceitaCadastro: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsMedico, buscaOptionsMedico } = useMedico();
  const {
    criarReceita,
    buscarReceita,
    atualizarReceita,
    alterarStatus,
    adicionarMedicamento,
    removerMedicamento,
    atualizarMedicamento,
    loading: loadingReceita,
  } = useReceita();

  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [medicamentos, setMedicamentos] = useState<IMedicamento[]>([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [medicoSelecionado, setMedicoSelecionado] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [receitaStatus, setReceitaStatus] = useState<
    'Criada' | 'Emitida' | 'Cancelada'
  >('Criada');
  const [receitaExistente, setReceitaExistente] = useState<IReceita | null>(
    null,
  );

  const isEditing = !!id;
  const isMedico = user.UsuTipo === TIPO_USUARIO.MEDICO;
  const isAdminOuSupervisor = user.UsuTipo <= TIPO_USUARIO.SUPERVISOR;
  const canEdit = receitaStatus === 'Criada';

  const query = new URLSearchParams(location.search);
  const pacienteUrl = query.get('paciente');
  const medicoUrl = query.get('medico');

  useEffect(() => {
    if (isAdminOuSupervisor) {
      buscaOptionsMedico();
    }
  }, [buscaOptionsMedico, isAdminOuSupervisor]);

  useEffect(() => {
    if (isEditing && id) {
      carregarReceita(Number(id));
    } else if (pacienteUrl) {
      setPacienteSelecionado({ value: pacienteUrl, label: '' });
      buscarPacienteDados(pacienteUrl);
    }

    if (medicoUrl) {
      setMedicoSelecionado({ value: medicoUrl, label: '' });
      buscarMedicoDados(medicoUrl);
    } else if (isMedico) {
      setMedicoSelecionado({
        value: user.UsuCodigo.toString(),
        label: user.UsuNome,
      });
      preencherDadosMedico(user.UsuCodigo.toString());
    }
  }, [id, pacienteUrl, medicoUrl, isEditing, isMedico, user]);

  const carregarReceita = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        const receita = await buscarReceita(codigo);
        setReceitaExistente(receita);
        setReceitaStatus(receita.RecStatus);
        setMedicamentos(receita.medicamentos || []);

        setPacienteSelecionado({
          value: receita.RecPacCodigo.toString(),
          label: receita.RecPacNome,
        });
        setMedicoSelecionado({
          value: receita.RecUsuCodigo.toString(),
          label: receita.RecUsuNome,
        });

        formRef.current?.setFieldValue(
          'pacienteID',
          receita.RecPacCodigo.toString(),
        );
        formRef.current?.setFieldValue(
          'medicoID',
          receita.RecUsuCodigo.toString(),
        );
        formRef.current?.setFieldValue('RecPacNome', receita.RecPacNome);
        formRef.current?.setFieldValue(
          'RecPacTelefone',
          formataTelefone(receita.RecPacTelefone || ''),
        );
        formRef.current?.setFieldValue(
          'RecPacCPF',
          formataCPF(receita.RecPacCPF || ''),
        );
        formRef.current?.setFieldValue(
          'RecPacEndereco',
          receita.RecPacEndereco || '',
        );
        formRef.current?.setFieldValue('RecUsuNome', receita.RecUsuNome);
        formRef.current?.setFieldValue('RecUsuCRM', receita.RecUsuCRM || '');
        formRef.current?.setFieldValue(
          'RecUsuEndereco',
          receita.RecUsuEndereco || '',
        );
        formRef.current?.setFieldValue(
          'RecInformacao',
          receita.RecInformacao || '',
        );
      } catch (error) {
        history.push('/receitas');
      } finally {
        setLoading(false);
      }
    },
    [buscarReceita, history, id],
  );

  const buscarPacienteDados = useCallback(async (pacCodigo: string) => {
    try {
      const response = await api.get(`/pacientes/${pacCodigo}`);
      const paciente = response.data;
      formRef.current?.setFieldValue('RecPacNome', paciente.PacNome);
      formRef.current?.setFieldValue(
        'RecPacTelefone',
        paciente.PacCelular || '',
      );
      formRef.current?.setFieldValue('RecPacCPF', paciente.PacCPF || '');
      formRef.current?.setFieldValue(
        'RecPacEndereco',
        `${paciente.PacEndereco || ''}, ${paciente.PacNumero || ''} ${
          paciente.PacBairro || ''
        } - ${paciente.PacCidade || ''}/${paciente.PacUF || ''}`.trim(),
      );
    } catch (error) {
      // Ignore error, user can fill manually
    }
  }, []);

  const buscarMedicoDados = useCallback(async (usuCodigo: string) => {
    try {
      const response = await api.get(`/users/${usuCodigo}`);
      const medico = response.data;
      formRef.current?.setFieldValue('RecUsuNome', medico.UsuNome);
      formRef.current?.setFieldValue('RecUsuCRM', medico.UsuCRM || '');
      formRef.current?.setFieldValue(
        'RecUsuEndereco',
        medico.UsuEndereco || '',
      );
    } catch (error) {
      // Ignore error
    }
  }, []);

  const preencherDadosMedico = useCallback(async (usuCodigo: string) => {
    try {
      const response = await api.get(`/medicos/${usuCodigo}`);
      const medico = response.data;
      formRef.current?.setFieldValue('RecUsuNome', medico.UsuNome);
      formRef.current?.setFieldValue('RecUsuCRM', medico.UsuCRM || '');
      formRef.current?.setFieldValue(
        'RecUsuEndereco',
        medico.UsuEndereco || '',
      );
    } catch (error) {
      // Ignore error
    }
  }, []);

  const handleSelectPaciente = useCallback(
    ({ value }: { value: string; label: string }) => {
      setPacienteSelecionado({ value, label: '' });
      buscarPacienteDados(value);
    },
    [],
  );

  const handleSelectMedico = useCallback(
    ({ value }: { value: string; label: string }) => {
      setMedicoSelecionado({ value, label: '' });
      buscarMedicoDados(value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (dados: IFormReceita) => {
      setLoading(true);
      try {
        formRef.current?.setErrors({});

        if (!pacienteSelecionado?.value) {
          addToast({ type: 'error', title: 'Paciente é obrigatório' });
          return;
        }
        if (!medicoSelecionado?.value) {
          addToast({ type: 'error', title: 'Médico é obrigatório' });
          return;
        }
        if (!dados.RecPacNome) {
          addToast({ type: 'error', title: 'Nome do paciente é obrigatório' });
          return;
        }
        if (!dados.RecPacTelefone) {
          addToast({
            type: 'error',
            title: 'Telefone do paciente é obrigatório',
          });
          return;
        }
        if (!dados.RecPacCPF) {
          addToast({ type: 'error', title: 'CPF do paciente é obrigatório' });
          return;
        }
        if (!dados.RecUsuNome) {
          addToast({ type: 'error', title: 'Nome do médico é obrigatório' });
          return;
        }

        const receitaData: ICreateReceitaDTO | IUpdateReceitaDTO = {
          RecPacCodigo: Number(pacienteSelecionado.value),
          RecUsuCodigo: Number(medicoSelecionado.value),
          RecPacNome: dados.RecPacNome,
          RecPacTelefone: dados.RecPacTelefone || undefined,
          RecPacCPF: dados.RecPacCPF || undefined,
          RecPacEndereco: dados.RecPacEndereco || undefined,
          RecUsuNome: dados.RecUsuNome,
          RecUsuCRM: dados.RecUsuCRM || undefined,
          RecUsuEndereco: dados.RecUsuEndereco || undefined,
          RecInformacao: dados.RecInformacao || undefined,
          medicamentos: medicamentos.map(m => ({
            MedNome: m.MedNome,
            MedQuantidade: m.MedQuantidade,
            MedObservacao: m.MedObservacao,
            MedControlado: m.MedControlado,
          })),
        };

        let receita: IReceita;

        if (isEditing && receitaExistente) {
          receita = await atualizarReceita(
            receitaExistente.RecCodigo,
            receitaData as IUpdateReceitaDTO,
          );
        } else {
          receita = await criarReceita(receitaData as ICreateReceitaDTO);
        }

        if (receita) {
          addToast({
            type: 'success',
            title: isEditing ? 'Receita atualizada' : 'Receita criada',
          });
          history.push(`/receita/${receita.RecCodigo}`);
        }
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      criarReceita,
      atualizarReceita,
      history,
      isEditing,
      medicamentos,
      pacienteSelecionado,
      medicoSelecionado,
      receitaExistente,
    ],
  );

  const handleEmitir = useCallback(async () => {
    if (!receitaExistente) return;

    if (medicamentos.length === 0) {
      addToast({
        type: 'error',
        title: 'Adicione pelo menos um medicamento antes de emitir',
      });
      return;
    }

    if (
      !window.confirm(
        'Tem certeza que deseja emitir esta receita? Após emitida, não poderá mais ser editada.',
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const receita = await alterarStatus(
        receitaExistente.RecCodigo,
        'Emitida',
      );
      setReceitaStatus('Emitida');
      setReceitaExistente(receita);
      addToast({ type: 'success', title: 'Receita emitida com sucesso' });
      history.push(`/receita/${receitaExistente.RecCodigo}`);
    } finally {
      setLoading(false);
    }
  }, [addToast, alterarStatus, receitaExistente, medicamentos.length, history]);

  const handleCancelar = useCallback(async () => {
    if (!receitaExistente) return;

    if (!window.confirm('Tem certeza que deseja cancelar esta receita?')) {
      return;
    }

    setLoading(true);
    try {
      // aqui pode ir para rota alterar status pois não foi gerado assinatura, então é simples
      const receita = await alterarStatus(
        receitaExistente.RecCodigo,
        'Cancelada',
      );
      setReceitaStatus('Cancelada');
      setReceitaExistente(receita);
      history.push(`/receita/${receitaExistente.RecCodigo}`);
    } finally {
      setLoading(false);
    }
  }, [alterarStatus, history, receitaExistente]);

  const handleVoltar = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleMedicamentoAtualizado = useCallback(
    (medicamento: IMedicamento) => {
      if (
        receitaExistente &&
        medicamento.MedCodigo &&
        medicamento.MedCodigo > 10000000000
      ) {
        // Temporary ID - need to save to backend
        const {
          MedNome,
          MedQuantidade,
          MedObservacao,
          MedControlado,
        } = medicamento;

        adicionarMedicamento(receitaExistente.RecCodigo, {
          MedNome,
          MedQuantidade,
          MedObservacao,
          MedControlado,
        } as ICreateMedicamentoDTO)
          .then(saved => {
            setMedicamentos(prev =>
              prev.map(m =>
                m.MedCodigo === medicamento.MedCodigo ? saved : m,
              ),
            );
          })
          .catch(() => {
            // Error handled in hook
          });
      }
    },
    [adicionarMedicamento, receitaExistente],
  );

  const handleMedicamentoSalvo = useCallback(
    (medicamento: IMedicamento) => {
      if (medicamento.MedCodigo) {
        // Editing existing
        setMedicamentos(prev =>
          prev.map(m =>
            m.MedCodigo === medicamento.MedCodigo ? medicamento : m,
          ),
        );
        // Sync to backend if prescription exists and it's a real ID (not temp)
        if (receitaExistente && medicamento.MedCodigo < 10000000000) {
          const dadosAtualizacao: ICreateMedicamentoDTO = {
            MedNome: medicamento.MedNome,
            MedQuantidade: medicamento.MedQuantidade,
            MedObservacao: medicamento.MedObservacao,
            MedControlado: medicamento.MedControlado,
          };
          atualizarMedicamento(
            receitaExistente.RecCodigo,
            medicamento.MedCodigo,
            dadosAtualizacao,
          )
            .then(saved => {
              setMedicamentos(prev =>
                prev.map(m =>
                  m.MedCodigo === medicamento.MedCodigo ? saved : m,
                ),
              );
            })
            .catch(() => {
              // Error handled in hook
            });
        }
      } else {
        // New medication - add with temporary ID

        const novoMedicamento = {
          ...medicamento,
          MedCodigo: Date.now(),
          MedRecCodigo: receitaExistente?.RecCodigo || 0,
          created_at: new Date(),
          updated_at: new Date(),
        };
        setMedicamentos(prev => [...prev, novoMedicamento]);
        // Sync to backend if prescription exists
        if (receitaExistente) {
          handleMedicamentoAtualizado(novoMedicamento);
        }
      }
    },
    [receitaExistente, handleMedicamentoAtualizado, atualizarMedicamento],
  );

  const handleMedicamentoRemovido = useCallback(
    (medCodigo: number): void => {
      if (receitaExistente && medCodigo < 10000000000) {
        // Real ID - remove from backend
        removerMedicamento(receitaExistente.RecCodigo, medCodigo).catch(() => {
          // Ignore error
        });
      }
      setMedicamentos(prev => prev.filter(m => m.MedCodigo !== medCodigo));
    },
    [removerMedicamento, receitaExistente],
  );

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Criada':
        return 'warning';
      case 'Emitida':
        return 'success';
      case 'Cancelada':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'Criada':
        return 'Criada';
      case 'Emitida':
        return 'Emitida';
      case 'Cancelada':
        return 'Cancelada';
      default:
        return status;
    }
  };

  return (
    <Container fluid className="mt-2">
      {(loading || loadingReceita) && <Loading />}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button variant="outline-secondary" onClick={handleVoltar}>
            <FaArrowLeft size={16} /> Voltar
          </Button>
        </div>
        <div>
          {isEditing && receitaExistente && (
            <>
              <Badge
                variant={getStatusColor(receitaStatus)}
                className="mr-2 px-3 py-2"
                style={{ fontSize: '0.875rem' }}
              >
                {getStatusLabel(receitaStatus)}
              </Badge>
            </>
          )}
        </div>
      </div>

      {!isEditing && <Titulo>Nova Receita</Titulo>}
      {isEditing && receitaExistente && (
        <Titulo>
          Editar Receita{' '}
          <Badge
            className="ml-2"
            variant={receitaExistente.RecControlado ? 'warning' : 'secondary'}
          >
            {receitaExistente.RecControlado ? 'Controlado' : 'Simples'}
          </Badge>
        </Titulo>
      )}

      {receitaStatus !== 'Criada' && (
        <StatusAlert
          variant={receitaStatus === 'Emitida' ? 'success' : 'danger'}
        >
          <strong>Receita {receitaStatus.toLowerCase()}</strong> - Não é
          possível alterar os dados ou medicamentos.
        </StatusAlert>
      )}

      <Form ref={formRef} onSubmit={handleSubmit}>
        <FormSection title="Paciente">
          <FormRow>
            {!isEditing && (
              <Col md={4} className="pr-1">
                <SelectAsync
                  name="pacienteID"
                  label="Nome do Paciente"
                  urlApi="pacientes/combo?termoBusca="
                  placeholder="Digite para buscar"
                  handleSelectChange={handleSelectPaciente}
                />
              </Col>
            )}

            <Col md={3} className="pr-1">
              <Input
                name="RecPacNome"
                label="Nome do Paciente *"
                maxLength={100}
                disabled={!canEdit}
              />
            </Col>
            <Col md={2} className="pr-1">
              <Input
                name="RecPacTelefone"
                label="Telefone"
                placeholder="(99) 99999-9999"
                maxLength={15}
                disabled={!canEdit}
              />
            </Col>

            <Col md={2} className="pr-1">
              <Input
                name="RecPacCPF"
                label="CPF"
                placeholder="999.999.999-99"
                maxLength={14}
                disabled={!canEdit}
              />
            </Col>
            <Col md={5} className="pr-1">
              <Input
                name="RecPacEndereco"
                label="Endereço"
                maxLength={200}
                disabled={!canEdit}
              />
            </Col>
          </FormRow>
        </FormSection>

        <FormSection title="Médico">
          <FormRow>
            {isAdminOuSupervisor && !isEditing && (
              <Col md={4} className="pr-1">
                <SelectSync
                  name="medicoID"
                  label="Médico *"
                  options={optionsMedico}
                  isClearable
                  handleSelectChange={handleSelectMedico}
                  isDisabled={!canEdit}
                />
              </Col>
            )}
            <Col
              md={isAdminOuSupervisor && !isEditing ? 4 : 4}
              className="pr-1"
            >
              <Input
                name="RecUsuNome"
                label="Nome do Médico *"
                maxLength={100}
                disabled={!canEdit}
              />
            </Col>
            <Col md={2} className="pr-1">
              <Input
                name="RecUsuCRM"
                label="CRM"
                maxLength={20}
                disabled={!canEdit}
              />
            </Col>

            <Col md={6}>
              <Input
                name="RecUsuEndereco"
                label="Endereço do Médico/Clínica"
                maxLength={200}
                disabled={!canEdit}
              />
            </Col>
          </FormRow>
        </FormSection>

        <FormSection title="Observações Gerais">
          <FormRow>
            <Col md={12}>
              <TextArea
                name="RecInformacao"
                label="Informações Adicionais"
                placeholder="Observações gerais da receita"
                maxLength={500}
                rows={3}
                disabled={!canEdit}
              />
            </Col>
          </FormRow>
        </FormSection>
      </Form>

      <FormSection title="Medicamentos">
        <MedicamentoLista
          medicamentos={medicamentos}
          receitaStatus={receitaStatus}
          receitaCodigo={receitaExistente?.RecCodigo || 0}
          onUpdateMedicamento={handleMedicamentoSalvo}
          onDeleteMedicamento={handleMedicamentoRemovido}
        />
      </FormSection>

      <ActionsBar>
        <Button type="button" variant="secondary" onClick={handleVoltar}>
          <FaArrowLeft size={16} /> Voltar
        </Button>

        {isEditing &&
          receitaExistente &&
          receitaExistente.RecStatus === 'Criada' && (
            <a
              className="btn btn-outline-primary"
              href={`/receita/imprimir/${id}`}
              target="_blank"
              rel="noreferrer"
            >
              <FaPrint size={16} /> Pré Visualizar
            </a>
          )}

        {isEditing && receitaExistente && (
          <>
            {receitaStatus === 'Criada' && (
              <>
                <Button
                  type="button"
                  variant="success"
                  onClick={handleEmitir}
                  disabled={loading || medicamentos.length === 0}
                >
                  <FaCheck size={16} /> Emitir Receita
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleCancelar}
                  disabled={loading}
                >
                  <FaTimes size={16} /> Cancelar Receita
                </Button>
              </>
            )}
            {receitaStatus === 'Emitida' && (
              <Button
                type="button"
                variant="danger"
                onClick={handleCancelar}
                disabled={loading}
              >
                <FaTimes size={16} /> Cancelar Receita
              </Button>
            )}
          </>
        )}

        {!isEditing && (
          <Button
            type="button"
            variant="success"
            disabled={loading || !canEdit}
            onClick={() => formRef.current?.submitForm()}
          >
            <FaSave size={16} /> {loading ? 'Salvando...' : 'Salvar Receita'}
          </Button>
        )}

        {isEditing && receitaStatus === 'Criada' && (
          <Button
            type="button"
            variant="primary"
            disabled={loading || !canEdit}
            onClick={() => formRef.current?.submitForm()}
          >
            <FaSave size={16} /> {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        )}
      </ActionsBar>
    </Container>
  );
};

export default ReceitaCadastro;
